import React from "react";
import { useResponsive } from "@outplayed/responsive";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { useWowPath, getEncounterId } from "../../../hooks/path-hooks";
import {
  getFormattedNames,
  getIsHealer,
  PATCH,
  getIsTank,
  getDifficultyPath,
  getFormattedEncounterType,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  EXPANSION,
  getAbbreviatedClass,
  getAbbreviatedSpec,
} from "../../../lib/wowHelpers";
import GemEnchantContainer from "./GemEnchantContainer";
import { useQueryString } from "../../../hooks/general-hooks";
import BestEnchants from "./BestEnchants";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import NoData from "../../Common/NoData";
import Layout from "../Layout";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";

const GemEnchantPageContainer = () => {
  const { currentBreakpoint } = useResponsive();
  const isMobile =
    currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM" || currentBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { difficulty, affix } = useQueryString();
  const difficultyPath = getDifficultyPath(difficulty, encounterType);
  const { data: gemTable, loading: isGemLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${difficultyPath}/${dataClassName}/${dataClassName}_gems.json`,
    { ssr: true },
  );
  const { data: enchantTable, loading: isEnchantLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${difficultyPath}/${dataClassName}/${dataClassName}_enchants.json`,
    { ssr: true },
  );

  if (isGemLoading || isEnchantLoading) {
    return null;
  }

  const dataClassSpec = `${dataClassName}-${dataSpecName}`;
  const hasGemData =
    gemTable &&
    (affix && affix !== "all"
      ? (gemTable.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.gems?.gem_counts?.length ?? 0) > 0
      : (gemTable?.[encounterType]?.[encounterID]?.[dataClassSpec]?.gems?.gem_counts?.length ?? 0) > 0);
  const hasEnchantData =
    enchantTable &&
    (affix && affix !== "all"
      ? Object.values(enchantTable.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.enchants ?? {}).length > 0
      : Object.values(enchantTable[encounterType]?.[encounterID]?.[dataClassSpec]?.enchants ?? {}).length > 0);

  const classSpecGems =
    hasGemData &&
    (affix && affix !== "all"
      ? gemTable.affixes[affix][encounterID][dataClassSpec].gems
      : gemTable[encounterType][encounterID][dataClassSpec].gems);
  const classSpecEnchants =
    hasEnchantData &&
    (affix && affix !== "all"
      ? enchantTable.affixes[affix][encounterID][dataClassSpec]
      : enchantTable[encounterType][encounterID][dataClassSpec]);
  const isHealer = getIsHealer(displaySpecName);
  const isTank = getIsTank(displaySpecName);

  const pageDescription = `${displaySpecName} ${displayClassName} with U.GG's best data for Enchants and Gems. The best ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} Enchants and Gems, data-driven by top players in ${EXPANSION} ${PATCH}.`;
  const metaDescription = `${displaySpecName} ${displayClassName} with U.GG's best data for Enchants and Gems. The best ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} Enchants and Gems, data-driven by top players in ${EXPANSION}.`;

  return (
    <Layout
      className="wow-gems-and-enchants-container"
      classType={classType}
      filterPage="builds"
      metaTitle={`${displaySpecName} ${displayClassName} Enchants and Gems for ${getFormattedEncounterType(encounterType)}`}
      metaDescription={metaDescription}
      pageTitle={`Enchants and Gems for ${getFormattedEncounterType(encounterType)}`}
      pageDescription={pageDescription}
      specType={specType}
    >
      {hasEnchantData && hasGemData ? (
        <>
          <BestEnchants
            displayClassName={displayClassName}
            displaySpecName={displaySpecName}
            enchants={classSpecEnchants?.enchants}
          />
          <GemEnchantContainer
            classSpecGems={classSpecGems}
            classSpecEnchants={classSpecEnchants}
            isMobile={isMobile}
            isHealer={isHealer}
            isTank={isTank}
            encounterType={encounterType}
            displayClassName={displayClassName}
            displaySpecName={displaySpecName}
          />
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to suit up your ${displaySpecName} ${displayClassName} with the best gems, enchants, and consumables.`}
            guideData={[
              {
                description: `The best in-depth guides covering gems, enchants, and consumables for ${displaySpecName} ${displayClassName}`,
                link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                  classType,
                )}-pve-${getIcyVeinsRole(specType)}-gems-enchants-consumables`,
                title: "Gems, Enchants, and Consumables Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default GemEnchantPageContainer;
